import React, { useState } from 'react';
import {
    IconButton,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Button,
    Input,
    Textarea,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Text,
    Box,
    useDisclosure,
    Flex,
    useToast
} from '@chakra-ui/react';
import { FaShareAlt } from 'react-icons/fa';
import useServerApi from 'hooks/useServerApi';

const BASE_URL = process.env.REACT_APP_BASE_URL || window.location.origin;

const ShowCallInfo = ({ call }) => {
    const startDate = new Date(call.start_time);
    const date = startDate.toLocaleDateString();
    const time = startDate.toLocaleTimeString();
    const durationSeconds = call.call_duration;
    const minutes = Math.floor(durationSeconds / 60);
    const seconds = durationSeconds % 60;
    const duration = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    const phone = call.caller;

    return (
        <Box p={3} bg="gray.50" borderRadius="md" fontSize="sm">
            <Text fontWeight="bold">Call Details</Text>
            <Text>Date: {date}</Text>
            <Text>Time: {time}</Text>
            <Text>Duration: {duration}</Text>
            {phone && <Text>Phone: {phone}</Text>}
        </Box>
    );
};

const ShareCallDetails = ({ call }) => {
    console.log(call);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [tabIndex, setTabIndex] = useState(0);
    const sharableLink = `${BASE_URL}/view-recording/${call.id}`;
    const toast = useToast();
    const [copied, setCopied] = useState(false);
    const [{ post }] = useServerApi();

    const validateEmails = (emailString) => {
        if (!emailString.trim()) return false;
        const emails = emailString.split(',').map(e => e.trim());
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emails.every(email => emailRegex.test(email));
    };

    const isValid = validateEmails(email) && message.trim();

    const handleCopy = () => {
        navigator.clipboard.writeText(sharableLink).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
            toast({ status: 'success', title: 'Link copied!' });
        });
    };

    const handleSend = async () => {
        const [success, result] = await post(`/rest_endpoints/call_history/share/${call.id}`, { email, message });
        if (success) {
            toast({ status: 'success', title: 'Email sent successfully!' });
        } else {
            toast({ status: 'error', title: 'Failed to send email' });
        }
        onClose();
    };

    return (
        <>
            <IconButton
                aria-label="Share call details"
                icon={<FaShareAlt />}
                size="sm"
                ml={1}
                onClick={onOpen}
                title="Share call details"
            />
            <Modal isOpen={isOpen} onClose={onClose} size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Share Call Information</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Tabs index={tabIndex} onChange={setTabIndex}>
                            <TabList>
                                <Tab>Email</Tab>
                                <Tab>Shareable Link</Tab>
                            </TabList>
                            <TabPanels>
                                <TabPanel>
                                    <Text mb={2}>Share call recording, transcript, and summary with your team members.</Text>
                                    <Input
                                        placeholder="email@example.com, email2@example.com"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        mb={3}
                                    />
                                    <Textarea
                                        placeholder="Here's the call information you requested..."
                                        value={message}
                                        onChange={e => setMessage(e.target.value)}
                                        rows={3}
                                        mb={3}
                                    />
                                    <ShowCallInfo call={call} />
                                </TabPanel>
                                <TabPanel>
                                    <Text mb={2}>Share this link to view the call recording, transcript, and summary.</Text>
                                    <Flex mb={3} align="center">
                                        <Input value={sharableLink} isReadOnly mr={2} />
                                        <Button size="sm" onClick={handleCopy} colorScheme={copied ? 'green' : 'blue'}>
                                            {copied ? 'Copied!' : 'Copy'}
                                        </Button>
                                    </Flex>
                                    <ShowCallInfo call={call} />
                                </TabPanel>
                            </TabPanels>
                        </Tabs>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                        {tabIndex === 0 ? (
                            <Button colorScheme="blue" onClick={handleSend} isDisabled={!isValid}>Send</Button>
                        ) : null}
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default ShareCallDetails;
